import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queries } from '../../../queries';

export type TranscriptState = 'loading' | 'ready' | 'missing' | 'error';

export interface AttachmentTranscript {
  readonly state: TranscriptState;
  readonly text: string;
}

/**
 * Loads a recording's transcript once `enabled` (the panel is open).
 *
 * A transcript is written once and never changes, so a found one is cached
 * for good. "Not written yet" is cached as stale instead: data-extractor may
 * write it at any moment, so reopening the panel asks again.
 */
export const useAttachmentTranscript = (
  docId: string,
  url: string | undefined,
  enabled: boolean
): AttachmentTranscript => {
  const queryClient = useQueryClient();
  const options = queries.cmail.attachmentTranscript(docId, url ?? '');
  const found = Boolean(queryClient.getQueryData(options.queryKey));

  const query = useQuery({
    ...options,
    enabled: enabled && Boolean(url),
    retry: false,
    staleTime: found ? Infinity : 0,
    refetchOnWindowFocus: false,
  });

  if (query.isError) {
    return { state: 'error', text: '' };
  }
  if (query.isSuccess) {
    return query.data
      ? { state: 'ready', text: query.data }
      : { state: 'missing', text: '' };
  }
  return { state: 'loading', text: '' };
};
